/**
 *   atb1.<key-fingerprint>.<claim-hash>.<base64 ciphertext>
 *
 * Normative spec: `core/src/crypto_envelope.rs`. This mirrors it for the browser.
 */

const PREFIX = "atb1";
const SEPARATOR = ".";

export interface Envelope {
  /** First 8 bytes of the recipient public key, hex. May be empty. */
  keyFingerprint: string;
  /** Commitment to the accompanying plaintext claims. May be empty. */
  claimHash: string;
  /** Raw ECIES payload. */
  payload: Uint8Array;
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function fromBase64(text: string): Uint8Array {
  const binary = atob(text);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

export function packEnvelope(
  payload: Uint8Array,
  keyFingerprint = "",
  claimHash = "",
): string {
  return [PREFIX, keyFingerprint, claimHash, toBase64(payload)].join(SEPARATOR);
}

function fieldWidthOk(value: string, hexChars: number): boolean {
  return value.length === 0
    ? true
    : value.length === hexChars && /^[0-9a-f]+$/.test(value);
}

/**
 * Stays true for a truncated envelope that `parseEnvelope` rejects — a severed
 * ciphertext is not plaintext, so callers must show a placeholder.
 */
export function isEnvelope(value: string): boolean {
  if (typeof value !== "string" || !value.startsWith(PREFIX + SEPARATOR)) return false;
  const parts = value.split(SEPARATOR);
  return parts.length >= 4 && fieldWidthOk(parts[1], 16) && fieldWidthOk(parts[2], 64);
}

/** Null, not a throw — pre-encryption records are plaintext. */
export function parseEnvelope(value: string): Envelope | null {
  if (!isEnvelope(value)) return null;
  const [, keyFingerprint, claimHash, ...rest] = value.split(SEPARATOR);
  try {
    return { keyFingerprint, claimHash, payload: fromBase64(rest.join(SEPARATOR)) };
  } catch {
    return null;
  }
}

export function keyFingerprintOf(pubKeyHex: string): string {
  return pubKeyHex.trim().replace(/^0x/i, "").toLowerCase().slice(0, 16);
}
